import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, Building2, Bell, Plug, Check } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { money } from "../../lib/format";
import { PageHeader, Loading, Field, ErrBox, StatusBadge, Table, Empty } from "../../components/common";
import { PROVIDER_LABELS } from "../../lib/format";

const TABS = [["business", "Business"], ["financial", "Financial & Reserves"], ["integrations", "Integrations"], ["billing", "Billing & Plan"]];

export default function PortalSettings() {
  const { reload } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "business");
  const [profile, setProfile] = useState(null);
  const [fin, setFin] = useState({ bank_balance: "", payroll_reserve: "", other_reserves: "" });
  const [integrations, setIntegrations] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [plans, setPlans] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [einNew, setEinNew] = useState("");

  const load = () => {
    API.get("/business/profile").then((r) => {
      setProfile(r.data.business);
      const f = r.data.business.financials || {};
      setFin({ bank_balance: (f.bank_balance_cents || 0) / 100, payroll_reserve: (f.payroll_reserve_cents || 0) / 100, other_reserves: (f.other_reserves_cents || 0) / 100 });
    });
    API.get("/integrations").then((r) => setIntegrations(r.data.integrations));
    API.get("/billing/ledger").then((r) => setLedger(r.data));
    API.get("/public/plans").then((r) => setPlans(r.data.plans.filter((p) => !p.custom)));
  };
  useEffect(() => { load(); }, []);

  const saveProfile = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await API.patch("/business/profile", { legal_name: profile.legal_name, dba: profile.dba, phone: profile.phone,
        website: profile.website, industry: profile.industry, filing_frequency: profile.filing_frequency,
        accounting_basis: profile.accounting_basis, fiscal_year: profile.fiscal_year,
        ...(einNew ? { ein: einNew } : {}) });
      setEinNew("");
      toast.success("Business profile saved");
      reload();
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  const saveFinancial = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await API.patch("/business/profile", { financials: fin });
      toast.success("Financial settings saved — Safe to Spend recalculated");
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  const setConnStatus = async (provider, status) => {
    try {
      await API.post(`/integrations/${provider}/status`, { status });
      toast.success(`${PROVIDER_LABELS[provider]} marked ${status.replace(/_/g, " ")}`);
      const r = await API.get("/integrations");
      setIntegrations(r.data.integrations);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const checkout = async (plan) => {
    setBusy(true); setErr("");
    try {
      const { data } = await API.post("/billing/checkout", {
        lookup_key: `${plan.slug}_${params.get("interval") || "monthly"}`,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e2) { setErr(errMsg(e2)); setBusy(false); }
  };

  if (!profile || !ledger || !integrations) return <Loading label="Loading settings" />;
  const set = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  return (
    <div data-testid="portal-settings-page">
      <PageHeader title="Settings" sub="Business profile, reserves, integrations, and subscription." />
      <div className="mb-5 flex flex-wrap gap-1 rounded-md border border-slate-200 bg-white p-1">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`settings-tab-${v}`}
            className={`rounded px-3 py-1.5 text-sm font-semibold ${tab === v ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
        ))}
      </div>

      {tab === "business" && (
        <form onSubmit={saveProfile} className="card-gov grid gap-4 p-6 sm:grid-cols-2" data-testid="business-form">
          <Field label="Legal Business Name"><input className="input-gov" value={profile.legal_name || ""} onChange={set("legal_name")} data-testid="set-legal-name" /></Field>
          <Field label="DBA"><input className="input-gov" value={profile.dba || ""} onChange={set("dba")} data-testid="set-dba" /></Field>
          <Field label="EIN" hint="Masked for security. Enter a new 9-digit EIN to update it.">
            <input className="input-gov font-mono" placeholder={profile.ein || "12-3456789"} value={einNew}
              onChange={(e) => setEinNew(e.target.value)} data-testid="set-ein" />
          </Field>
          <Field label="AZ TPT License (masked)"><input className="input-gov font-mono" value={profile.tpt_license || ""} disabled data-testid="set-tpt" /></Field>
          <Field label="Phone"><input className="input-gov" value={profile.phone || ""} onChange={set("phone")} data-testid="set-phone" /></Field>
          <Field label="Website"><input className="input-gov" value={profile.website || ""} onChange={set("website")} data-testid="set-website" /></Field>
          <Field label="Industry"><input className="input-gov" value={profile.industry || ""} onChange={set("industry")} data-testid="set-industry" /></Field>
          <Field label="Filing Frequency">
            <select className="input-gov" value={profile.filing_frequency || ""} onChange={set("filing_frequency")} data-testid="set-filing-frequency">
              <option value="">To be verified by administrator</option>
              {["monthly", "quarterly", "annual", "unsure"].map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
          </Field>
          <Field label="Accounting Basis">
            <select className="input-gov" value={profile.accounting_basis || "cash"} onChange={set("accounting_basis")} data-testid="set-basis">
              <option value="cash">Cash basis</option><option value="accrual">Accrual basis</option>
            </select>
          </Field>
          <Field label="Tax Year">
            <select className="input-gov" value={profile.fiscal_year || "calendar"} onChange={set("fiscal_year")} data-testid="set-fiscal">
              <option value="calendar">Calendar year</option><option value="fiscal">Fiscal year</option>
            </select>
          </Field>
          <div className="sm:col-span-2"><ErrBox msg={err} /></div>
          <button className="btn-navy sm:col-span-2" disabled={busy} data-testid="save-business">{busy ? "Saving…" : "Save Business Profile"}</button>
        </form>
      )}

      {tab === "financial" && (
        <form onSubmit={saveFinancial} className="card-gov max-w-2xl space-y-4 p-6" data-testid="financial-form">
          <p className="text-sm text-slate-600">These figures drive your <strong>Safe to Spend</strong> number: Bank balance − Tax reserves − Scheduled tax payments − Payroll reserves − Other required reserves.</p>
          <Field label="Current Bank Balance ($)"><input className="input-gov money" inputMode="decimal" value={fin.bank_balance} onChange={(e) => setFin({ ...fin, bank_balance: e.target.value })} data-testid="fin-bank" /></Field>
          <Field label="Payroll Tax Reserve ($)"><input className="input-gov money" inputMode="decimal" value={fin.payroll_reserve} onChange={(e) => setFin({ ...fin, payroll_reserve: e.target.value })} data-testid="fin-payroll" /></Field>
          <Field label="Other Required Reserves ($)"><input className="input-gov money" inputMode="decimal" value={fin.other_reserves} onChange={(e) => setFin({ ...fin, other_reserves: e.target.value })} data-testid="fin-other" /></Field>
          <ErrBox msg={err} />
          <button className="btn-navy" disabled={busy} data-testid="save-financial">{busy ? "Saving…" : "Save & Recalculate"}</button>
        </form>
      )}

      {tab === "integrations" && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="integrations-grid">
          {integrations.map((i) => (
            <div key={i.provider} className="card-gov p-5" data-testid={`integration-${i.provider}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Plug className="h-4 w-4 text-navy" /><span className="font-bold text-navy">{PROVIDER_LABELS[i.provider]}</span></div>
                <StatusBadge status={i.status} />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {i.status === "CONNECTED" ? `Connected. Last sync: ${i.last_sync || "—"}`
                  : i.status === "ACTION_REQUIRED" ? "Connection needs attention — credentials or authorization required."
                  : "Not connected. API credentials can be added by an administrator."}
              </p>
              <div className="mt-3 flex gap-2">
                {i.status === "NOT_CONNECTED" && (
                  <button className="btn-outline-gov flex-1 px-2 py-1.5 text-xs" onClick={() => setConnStatus(i.provider, "ACTION_REQUIRED")} data-testid={`connect-${i.provider}`}>Begin Connection</button>
                )}
                {i.status === "ACTION_REQUIRED" && (
                  <button className="btn-outline-gov flex-1 px-2 py-1.5 text-xs" onClick={() => setConnStatus(i.provider, "NOT_CONNECTED")} data-testid={`disconnect-${i.provider}`}>Reset</button>
                )}
              </div>
              <p className="mt-2 text-[10px] text-slate-400">We never show a connection as live until real API credentials are verified.</p>
            </div>
          ))}
        </div>
      )}

      {tab === "billing" && (
        <div className="space-y-6" data-testid="billing-tab">
          <div className="card-gov flex flex-wrap items-center justify-between gap-4 p-6">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Current Plan</div>
              <div className="mt-1 text-2xl font-bold text-navy" data-testid="current-plan">{ledger.plan ? ledger.plan.name : "No active plan"}</div>
              <div className="mt-1 flex items-center gap-2">
                <StatusBadge status={ledger.subscription?.status || "none"} />
                {ledger.credit_cents > 0 && <span className="text-sm font-semibold text-emerald-700">Account credit: {money(ledger.credit_cents, { cents: true })}</span>}
              </div>
            </div>
            <CreditCard className="h-10 w-10 text-slate-200" />
          </div>

          <div>
            <h3 className="mb-3 font-bold text-navy">Available Plans</h3>
            <div className="grid gap-4 sm:grid-cols-3">
              {plans.map((p) => (
                <div key={p.id} className={`card-gov p-4 ${ledger.plan?.id === p.id ? "border-2 border-gold" : ""}`} data-testid={`billing-plan-${p.slug}`}>
                  <div className="font-bold text-navy">{p.name}</div>
                  <div className="money mt-1 text-xl font-bold text-slate-800">{money(p.price_monthly_cents)}<span className="text-xs text-slate-400">/mo</span></div>
                  <ul className="mt-2 space-y-1">
                    {(p.features || []).slice(0, 4).map((f) => <li key={f} className="flex gap-1.5 text-xs text-slate-500"><Check className="h-3 w-3 shrink-0 text-emerald-600 mt-0.5" />{f}</li>)}
                  </ul>
                  {ledger.plan?.id === p.id ? (
                    <div className="mt-3 rounded bg-emerald-50 px-2 py-1.5 text-center text-xs font-bold text-emerald-700">CURRENT PLAN</div>
                  ) : (
                    <button className="btn-navy mt-3 w-full py-1.5 text-xs" onClick={() => checkout(p)} disabled={busy} data-testid={`subscribe-${p.slug}`}>
                      {busy ? "Redirecting…" : "Subscribe with Stripe"}
                    </button>
                  )}
                </div>
              ))}
            </div>
            <ErrBox msg={err} />
          </div>

          <div>
            <h3 className="mb-3 font-bold text-navy">Billing Ledger</h3>
            {ledger.invoices.length === 0 && ledger.payments.length === 0 ? (
              <Empty title="No billing activity yet" msg="Subscription charges, credits, and payments appear here — kept completely separate from your government tax payments." />
            ) : (
              <Table headers={["Date", "Description", "Amount", "Status"]} testid="ledger-table">
                {[...ledger.invoices.map((i) => ({ date: i.created_at, desc: i.description, amt: i.amount_cents, status: i.status })),
                  ...ledger.payments.map((p) => ({ date: p.created_at, desc: `Stripe checkout (${p.lookup_key})`, amt: p.amount_cents, status: p.payment_status }))]
                  .sort((a, b) => (b.date > a.date ? 1 : -1)).map((r, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-4 py-2.5 text-slate-500">{new Date(r.date).toLocaleDateString()}</td>
                      <td className="px-4 py-2.5">{r.desc}</td>
                      <td className="px-4 py-2.5 money font-semibold">{money(r.amt, { cents: true })}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={r.status === "paid" ? "PAID" : r.status} /></td>
                    </tr>
                  ))}
              </Table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
